<template>
  <FusionBaseDialog
    :open="open"
    :title="downloading || finished ? '' : $t('map.historyDialog.exportDialog.title')"
    :persistentOverlay="true"
    :icon="downloading || finished ? '' : 'mdi-file-download'"
    @close="close"
  >
    <div class="export-data-dialog">
      <!-- Finished Downloading -->
      <v-card-subtitle v-if="finished" class="finished" flat>
        <div class="exportComplete" data-cy="exportComplete">
          {{ $t('map.historyDialog.exportDialog.exportComplete') }}
        </div>
        <div data-cy="filesAvailable">
          {{
            $t('map.historyDialog.exportDialog.filesAvailable', {
              exportedFiles: devices.length - errorFiles,
              totalFiles: devices.length
            })
          }}
        </div>
        <div v-if="hasErrors" class="warningText" data-cy="warningText">
          {{ $t('map.historyDialog.exportDialog.failedExportWarning') }}
        </div>
      </v-card-subtitle>
      <!-- Downloading -->
      <v-card-subtitle v-if="downloading" class="downloading" flat>
        <div class="exportingCsv" data-cy="exportingCsv">
          {{ $t('map.historyDialog.exportDialog.exportingCsv') }}
        </div>
        <div class="wait" data-cy="wait">
          {{ $t('map.historyDialog.exportDialog.wait') }}
        </div>
        <div class="longExportInfo" data-cy="longExportInfo">
          {{ $t('map.historyDialog.exportDialog.longExportInfo') }}
        </div>
        <div class="exportingFile" data-cy="exportingFile">
          {{
            $t('map.historyDialog.exportDialog.exportingFile', {
              currentFile: currentFile,
              totalFiles: devices.length,
              fileSize: currentFileSizeConversion,
              fileSizeUnit: currentFileSizeUnits
            })
          }}
        </div>
        <div class="fromDevice" data-cy="fromDevice">
          {{
            $t('map.historyDialog.exportDialog.fromDevice', {
              deviceName: currentDeviceName,
              deviceSerial: currentDeviceSerial
            })
          }}
        </div>
        <v-progress-linear color="primary" indeterminate height="15" data-cy="progressBar">
        </v-progress-linear>
      </v-card-subtitle>
      <!-- Export Set Up -->
      <v-card-subtitle v-if="!downloading && !finished">
        <div>
          <span data-cy="maxExportDaysText">
            {{
              $t('map.historyDialog.exportDialog.subtitle', {
                capacity: $t('map.historyDialog.exportDialog.threeMonths')
              })
            }}
          </span>
        </div>
      </v-card-subtitle>
      <v-card v-if="!downloading && !finished" class="pt-6 pb-1" flat>
        <div class="form-section-header">
          {{ $t('map.historyDialog.exportDialog.dateRange') }}
        </div>
        <v-row justify="center" class="px-6">
          <DatePickerMenu
            v-model="dateStart"
            :label="$t('map.historyDialog.exportDialog.from')"
            :min="minDateStart"
            :max="maxDateStart"
          />
        </v-row>
        <v-row justify="center" class="px-6">
          <DatePickerMenu
            v-model="dateEnd"
            :label="$t('map.historyDialog.exportDialog.to')"
            :min="minDateEnd"
            :max="maxDateEnd"
          />
        </v-row>
      </v-card>
      <v-card-subtitle v-if="!downloading && !finished" flat>
        <div class="loggingIntervalInfo" data-cy="loggingIntervalInfo">
          <span>
            {{ $t('map.historyDialog.exportDialog.loggingIntervalInfo') }}
          </span>
        </div>
      </v-card-subtitle>
      <v-card-actions class="warning-container" text>
        <v-spacer></v-spacer>
        <div v-if="publicOnlyWarning && !downloading" class="hint-text red-font">
          {{ $t('map.historyDialog.exportDialog.publicOnlyWarning') }}
        </div>
        <div v-if="publicMixedWarning && !downloading" class="hint-text red-font">
          {{ $t('map.historyDialog.exportDialog.publicMixedWarning') }}
        </div>
      </v-card-actions>
    </div>
    <template v-if="!finished && !downloading" v-slot:actions>
      <v-btn @click="close" variant="outlined" class="text-black" data-cy="closeExportData">{{
        $t('buttons.cancel')
      }}</v-btn>
      <v-spacer></v-spacer>
      <v-btn
        @click="exportCsv"
        class="bg-primary text-white"
        variant="text"
        data-cy="exportDataButton"
        :disabled="publicOnlyWarning"
        >{{ $t('buttons.export') }}</v-btn
      >
    </template>
    <template v-else-if="downloading" v-slot:actions>
      <v-btn @click="cancel" variant="outlined" class="text-black" data-cy="cancelExportData">{{
        $t('buttons.cancel')
      }}</v-btn>
    </template>
    <template v-else v-slot:actions>
      <v-spacer></v-spacer>
      <v-btn
        v-if="finished"
        data-cy="closeExportDataDialogButton"
        @click="close"
        class="bg-primary text-white export-data-done-button"
        variant="text"
        >{{ $t('buttons.done') }}</v-btn
      >
    </template>
  </FusionBaseDialog>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import { getAuthInstance } from '@fusion/auth-v3'
import { FusionStores } from '@fusion/saas-components-v3'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import type { WidgetDevice } from '@/helpers/widgets/helper'
import { getTodayDateObject, formatDate } from '../../services/data-export'
import DatePickerMenu from '../common/DatePickerMenu.vue'
import { SlugsEnum } from '../../permissions/SlugsEnum'
import { getUserAccountId } from '../../helpers/loginas/logInAsHelper'

const ONE_DAY_MS = 1000 * 60 * 60 * 24
const NINETY_FOUR_DAYS_MS = ONE_DAY_MS * 94
const THREE_HUNDRED_NINETY_SEVEN_DAYS_MS = ONE_DAY_MS * 397

export default defineComponent({
  name: 'ExportDataDialog',
  components: { DatePickerMenu },
  props: {
    open: Boolean,
    devices: { type: Array as PropType<WidgetDevice[]>, required: true },
    permissions: { type: Object as PropType<Record<string, string[]>>, required: true }
  },
  emits: ['close'],
  setup() {
    const userProfile = FusionStores.userProfile(getAuthInstance())
    return { userProfile }
  },
  data() {
    return {
      dateStart: '',
      dateEnd: '',
      downloading: false,
      accountId: '',
      errorFiles: 0,
      currentFile: 1,
      currentDeviceName: '',
      currentDeviceSerial: '',
      currentFileSize: 0,
      finished: false,
      // Used to handle cancellation of export requests
      controller: new AbortController(),
      signal: null as AbortSignal | null
    }
  },
  async mounted() {
    this.accountId = await getUserAccountId(this.$auth)
    this.signal = this.controller.signal
    this.init()
  },
  computed: {
    permissionSet(): string[] {
      const perms = [...new Set(this.devices.flatMap((device) => this.permissions[device.id]))]
      return perms
    },
    hasDayPermission() {
      return this.permissionSet.includes(SlugsEnum.HistoryChartExecuteExportDay)
    },
    minDateStart(): string {
      return this.getMinDateStart()
    },
    minDateEnd(): string {
      return this.dateStart
    },
    maxDateStart(): string {
      let minTime = new Date(this.dateStart!).getTime()
      if (minTime < this.getDateToday().getTime()) {
        minTime = this.getDateToday().getTime()
      }

      return formatDate(minTime)
    },
    maxDateEnd(): string {
      let maxTime = new Date(this.dateStart!).getTime() + NINETY_FOUR_DAYS_MS
      if (maxTime > this.getDateToday().getTime()) {
        maxTime = this.getDateToday().getTime()
      }

      return formatDate(maxTime)
    },
    publicMixedWarning(): boolean {
      let foundMine = false
      let foundPublic = false

      for (const device of this.devices) {
        const isMine = this.getIsMineByDeviceId(device.id)
        const isShared = this.getIsSharedByDeviceId(device.id)

        if (isMine || isShared) {
          foundMine = true
        } else {
          foundPublic = true
        }
      }

      return foundMine && foundPublic
    },
    publicOnlyWarning(): boolean {
      let foundMine = false
      let foundPublic = false

      for (const device of this.devices) {
        const isMine = this.getIsMineByDeviceId(device.id)
        const isShared = this.getIsSharedByDeviceId(device.id)
        if (isMine || isShared) {
          foundMine = true
        } else {
          foundPublic = true
        }
      }

      return !foundMine && foundPublic
    },
    hasErrors() {
      return this.errorFiles > 0
    },
    currentFileSizeConversion() {
      if (this.currentFileSize >= 1000000) {
        // in MB
        return (this.currentFileSize / 1000000).toFixed(1)
      }
      // in KB
      return this.currentFileSize === 0 ? '0' : (this.currentFileSize / 1000).toFixed(1)
    },
    currentFileSizeUnits() {
      return this.currentFileSize >= 1000000 ? 'mb' : 'kb'
    }
  },
  watch: {
    open() {
      this.init()
    },
    dateStart() {
      if (new Date(this.dateStart!).getTime() > new Date(this.dateEnd!).getTime()) {
        this.dateEnd = this.dateStart
      }

      if (
        new Date(this.dateEnd!).getTime() > new Date(this.maxDateEnd).getTime() ||
        this.dateStart === this.dateEnd
      ) {
        this.dateEnd = this.maxDateEnd
      }
    },
    dateEnd() {
      if (new Date(this.dateStart!).getTime() > new Date(this.dateEnd!).getTime()) {
        this.dateStart = this.dateEnd
      }
    }
  },
  methods: {
    getDateToday(): Date {
      return getTodayDateObject()
    },
    init() {
      this.dateStart = formatDate(this.getDateToday().getTime() - ONE_DAY_MS)
      this.dateEnd = formatDate(this.getDateToday().getTime())
    },
    close() {
      this.$emit('close')

      // delay setting download to false to give time for the dialog to close
      setTimeout(() => {
        this.downloading = false
        this.finished = false
        this.errorFiles = 0
        this.currentFile = 1
        this.currentFileSize = 0
      }, 50)
    },
    async exportCsv() {
      this.downloading = true
      let current = -1
      let currentBytesReceived = 0

      const onProgress = (i: number, completed = false, bytesReceived = 0) => {
        if (completed) {
          return
        }
        if (i > current) {
          current = i
          currentBytesReceived = 0
        }
        currentBytesReceived += bytesReceived
        // To prevent it from hanging at 0kb at the start of the next file, we only update once we actually have a number
        // There will be a small delay to start updating the file size from file to file as a result
        if (currentBytesReceived !== 0) {
          this.currentFileSize = currentBytesReceived
        }
      }

      for (let i = 0; i < this.devices.length; i++) {
        const device = this.devices[i]
        const devId = device.id
        const isMine = this.getIsMineByDeviceId(devId)
        const isShared = this.getIsSharedByDeviceId(devId)
        if (isMine || isShared) {
          this.currentDeviceSerial =
            this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SERIAL_BY_DEVICE_ID}`](
              devId
            )
          this.currentDeviceName = device.friendlyName
          try {
            const measurements = this.$store.getters[
              `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SUPPORTED_MEASUREMENTS}`
            ](device.model, device.submodel)
            const devicePermissions = this.permissions[devId]
            const maxAllowedDeviceMS = this.getMaxAllowedDeviceMS(devicePermissions)
            const endDate = this.getEndDate()
            const startDate = this.getDeviceStartDate(endDate, maxAllowedDeviceMS)
            await this.$api.getCSV(
              devId,
              startDate.toISOString(),
              endDate.toISOString(),
              devicePermissions,
              measurements,
              this.signal,
              isShared,
              this.userProfile.settings.tempUnit,
              this.userProfile.settings.pressureUnit,
              this.userProfile.settings.pmUnit,
              (completed = false, bytesReceived = 0) => onProgress(i, completed, bytesReceived)
            )
          } catch (error) {
            // If cancelled in the middle of exporting, we want to end
            if (error instanceof Error && error.name === 'AbortError') {
              return
            }
            // eslint-disable-next-line no-console
            console.error(error)
            this.errorFiles++
          }
        }
        this.currentFile++
      }
      this.downloading = false
      this.finished = true
    },

    getIsMineByDeviceId(id: string) {
      return (
        this.accountId ===
        this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_ACCOUNT_BY_DEVICE_ID}`](id)
      )
    },
    getIsSharedByDeviceId(id: string) {
      return this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_SHARED_BY_DEVICE_ID}`
      ](id)
    },
    getMaxAllowedDeviceMS(devicePermissions: string[]) {
      let minTime = ONE_DAY_MS
      if (devicePermissions?.length) {
        minTime = NINETY_FOUR_DAYS_MS
      }

      return minTime
    },
    getDeviceStartDate(endDate: Date, maxAllowedDeviceMS: number): Date {
      const startDate = new Date(this.dateStart!)
      if (!Number.isNaN(endDate.getTime())) {
        const maxStartDate = new Date(new Date(this.dateEnd!).getTime() - maxAllowedDeviceMS)
        if (startDate.getTime() < maxStartDate.getTime()) {
          return maxStartDate
        }
      }

      return startDate
    },
    getEndDate() {
      return new Date(new Date(this.dateEnd).getTime() + ONE_DAY_MS - 1)
    },
    getMinDateStart() {
      return formatDate(this.getDateToday().getTime() - THREE_HUNDRED_NINETY_SEVEN_DAYS_MS)
    },
    cancel() {
      this.controller.abort()
      // Reset AbortController to allow future export requests
      this.controller = new AbortController()
      this.signal = this.controller.signal

      this.downloading = false
      this.finished = false
      this.errorFiles = 0
      this.currentFile = 1
      this.currentFileSize = 0
    }
  }
})
</script>

<style lang="scss" scoped>
.v-overlay {
  padding: 16px;
  .downloading,
  .finished {
    text-align: center;
    min-height: 300px !important;
    max-height: 300px !important;
  }
  .v-card {
    .v-input {
      padding-top: 24px;
    }
    .form-section-header {
      font-weight: 300;
      font-size: 18px;
      opacity: 0.56;
      padding-left: 12px;
      margin-bottom: 24px;
    }
    .message {
      color: rgba(0, 0, 0, 0.56);
    }
  }
  .v-card-subtitle {
    padding: 16px 12px 8px;
    text-wrap: wrap;
    color: black;
  }
  .v-card-title {
    font-weight: 300;
    padding: 0 0 16px 0;
  }
  .warning-container {
    height: 34px;
  }
  .v-btn {
    min-width: 120px !important;
    @media (min-width: 496px) {
      min-width: 160px !important;
    }
  }
  .hint-text {
    font-size: 12px;
    opacity: 0.76;
  }
  .hint-text.red-font,
  .warningText {
    color: red;
  }
  .warningText {
    font-weight: bold;
  }
  .exportingCsv {
    font-size: 20px;
  }
  .exportingFile,
  .wait {
    font-size: 20px;
    font-weight: bold;
  }
  .exportingFile {
    padding-top: 32px;
  }
  .wait {
    padding-top: 16px;
    padding-bottom: 16px;
  }
  .fromDevice {
    padding-bottom: 16px;
  }
  .exportComplete {
    font-size: 30px;
    font-weight: bold;
    padding: 64px 0px 24px;
  }
}
</style>
